"use client";

import * as React from "react";
import { Accordion } from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import { FaqContactSection } from "./faq-contact-section";
import { FaqItem } from "./faq-item";

interface FaqSectionWithCategoriesProps
  extends React.HTMLAttributes<HTMLElement> {
  title: string;
  description?: string;
  items: {
    question: string;
    answer: string;
    category?: string;
  }[];
  contactInfo?: {
    title: string;
    description?: string;
    buttonText: string;
  };
}

const FaqSectionWithCategories = React.forwardRef<
  HTMLElement,
  FaqSectionWithCategoriesProps
>(({ className, title, description, items, contactInfo, ...props }, ref) => {
  return (
    <section
      className={cn("w-full py-20 lg:py-24", className)}
      ref={ref}
      {...props}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          {/* Header */}
          <div className="mb-16 space-y-6 text-center">
            <h2 className="heading-display font-bold text-4xl text-foreground sm:text-5xl lg:text-6xl">
              {title}
            </h2>
            {description && (
              <p className="mx-auto max-w-3xl text-lg text-muted-foreground text-optimal sm:text-xl">
                {description}
              </p>
            )}
          </div>

          {/* FAQ Items */}
          <Accordion className="space-y-6" collapsible type="single">
            {items.map((item, index) => (
              <FaqItem
                answer={item.answer}
                category={item.category}
                key={index}
                question={item.question}
                value={`item-${index}`}
              />
            ))}
          </Accordion>

          {/* Contact Section */}
          {contactInfo && (
            <FaqContactSection
              buttonText={contactInfo.buttonText}
              description={contactInfo.description}
              title={contactInfo.title}
            />
          )}
        </div>
      </div>
    </section>
  );
});
FaqSectionWithCategories.displayName = "FaqSectionWithCategories";

export { FaqSectionWithCategories };
